import { useEffect, useState } from "react";
import { Phone, Mail } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_website-builder-1674/artifacts/923t2kyx_image.png";

export default function Header() {
  const { t, lang, setLang } = useT();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b border-white/10 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="grid grid-cols-12 items-center px-6 sm:px-10 h-20 sm:h-24">
        <a
          href="#top"
          data-testid="logo-link"
          className="col-span-6 md:col-span-3 flex items-center gap-3"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <img
            src={LOGO_URL}
            alt="S&K Facade Panels"
            className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
            data-testid="logo-mark"
          />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-[12px] font-bold tracking-[0.25em] uppercase text-white">
              S&amp;K Facade Panels
            </span>
            <span
              className="text-[9px] tracking-[0.22em] uppercase text-white/45 mt-0.5"
              data-testid="distributor-badge"
            >
              {t.distributor}
            </span>
          </div>
        </a>

        <nav className="hidden md:flex col-span-6 items-center justify-center gap-10 text-xs uppercase tracking-[0.18em] text-white/70">
          <button
            data-testid="nav-product"
            onClick={() => scrollTo("product")}
            className="hover:text-white transition-colors"
          >
            {t.nav.product}
          </button>
          <button
            data-testid="nav-finishes"
            onClick={() => scrollTo("finishes")}
            className="hover:text-white transition-colors"
          >
            {t.nav.finishes}
          </button>
          <button
            data-testid="nav-specs"
            onClick={() => scrollTo("specs")}
            className="hover:text-white transition-colors"
          >
            {t.nav.specs}
          </button>
          <button
            data-testid="nav-contact"
            onClick={() => scrollTo("contact")}
            className="hover:text-white transition-colors"
          >
            {t.nav.contact}
          </button>
        </nav>

        <div className="col-span-6 md:col-span-3 flex items-center justify-end gap-2">
          <div
            data-testid="language-toggle"
            className="inline-flex items-center border border-white/10 divide-x divide-white/10"
          >
            {["mk", "en", "sq"].map((code) => {
              const active = lang === code;
              return (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`lang-${code}`}
                  aria-label={`Switch to ${code.toUpperCase()}`}
                  aria-pressed={active}
                  className={`px-2.5 sm:px-3 py-2 text-[11px] uppercase tracking-[0.2em] transition-colors ${
                    active
                      ? "text-white bg-white/[0.08]"
                      : "text-white/45 hover:text-white"
                  }`}
                >
                  {code.toUpperCase()}
                </button>
              );
            })}
          </div>
          <a
            data-testid="header-phone"
            href="tel:+38978351715"
            className="hidden lg:inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.15em] text-white/80 hover:text-white border border-white/10 hover:border-white/40 transition-colors"
          >
            <Phone size={14} strokeWidth={1.5} />
            +389 78 351 715
          </a>
          <button
            data-testid="header-cta-quote"
            onClick={() => scrollTo("contact")}
            className="hidden sm:inline-flex items-center gap-2 bg-white text-black px-4 py-2 text-xs uppercase tracking-[0.18em] font-medium hover:bg-neutral-200 transition-colors"
          >
            <Mail size={14} strokeWidth={1.75} />
            {t.nav.requestQuote}
          </button>
        </div>
      </div>
    </header>
  );
}
